import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {NilFunction} from "../../../../shared/types";
import {NavToggleMenuHandle} from "../../NavToggleMenu/props";
import LanguageCurrencyPickerProps from "../../LanguageCurrencyPicker/props";
import {
  LanguageCurrencyFullScreenHandle
} from "../../LanguageCurrencyPicker/components/LanguageCurrencyFullScreen/props";
import {TypeAheadSearchFocusProps} from "../props";
import {definedValue} from "../../../../utils/input";

const parseSearchBoxProps = (searchBox?: TypeAheadSearchFocusProps): TypeAheadSearchFocusProps => {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const nil = () => {};
  if(searchBox) {
    return {
      onKeyUp: definedValue(searchBox.onKeyUp, nil),
      minCharacters: definedValue(searchBox.minCharacters, 0),
      options: definedValue(searchBox.options, []),
      bgPlaceholder: definedValue(searchBox.bgPlaceholder, ''),
      placeholder: definedValue(searchBox.placeholder, ''),
      notFoundText: definedValue(searchBox.notFoundText, ''),
      hideSuggestions: definedValue(searchBox.hideSuggestions, false),
      onSearchChange: definedValue(searchBox.onSearchChange, nil),
      search: definedValue(searchBox.search, ''),
    }
  }
  return {
    onKeyUp: nil,
    minCharacters: 0,
    options:  [],
    bgPlaceholder: '',
    placeholder: '',
    notFoundText: '',
    hideSuggestions: false,
    onSearchChange: nil,
    search: '',
  }
};

const useHeaderMenuSearch = (onClick?: NilFunction, searchBox?: TypeAheadSearchFocusProps, keywordSearch = '') => {
  const [searchValue, setSearchValue] = useState<string>(keywordSearch);
  const navMenuRef = useRef<NavToggleMenuHandle>(null);
  const headerCenterRef = useRef<HTMLDivElement>(null);
  const focusableRef = useRef<HTMLDivElement | null>(null);
  const clearRef = useRef<NilFunction | null>(null);
  const [focusedSearch, setFocusedSearch] = useState<boolean>(() => false);
  const [menuOpened, setMenuOpened] = useState<boolean>(() => false);
  const searchBoxProps = useMemo(() => parseSearchBoxProps(searchBox), [searchBox]);
  useEffect(() => {
    setSearchValue(keywordSearch);
  }, [keywordSearch]);

  const handleMenuClick = () => {
    if (focusedSearch) {
      return;
    }
    navMenuRef.current?.openMenu();
    onClick?.();
    setMenuOpened(true);
  }
  const enableInputFocus = () => {
    navMenuRef.current?.displayOverlay();
    headerCenterRef.current?.classList.add('above-nav-overlay');
    focusableRef.current?.classList.add('focus-in');
    setFocusedSearch(true);
  }
  const disableInputFocus = () => {
    if (focusedSearch) {
      setFocusedSearch(false);
      setSearchValue('');
      navMenuRef.current?.hideOverlay();
      headerCenterRef.current?.classList.remove('above-nav-overlay');
      focusableRef.current?.classList.remove('focus-in');
      return;
    }
    if (menuOpened) {
      navMenuRef.current?.closeMenu();
      setMenuOpened(false);
    }
  }
  const disableOverlayAndClear = () => {
    disableInputFocus();
    clearRef.current?.();
  }
  const onInputKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>, clearCallback?: NilFunction) => {
    if(!focusedSearch) {
      enableInputFocus();
      const value = e.currentTarget.value;
      setSearchValue(value);
    }
    if (['Escape', 'Enter'].includes(e.key)) {
      e.currentTarget.blur();
      disableOverlayAndClear();
    }
    searchBoxProps.onKeyUp?.(e);
    if (typeof clearCallback === 'function' && clearRef.current !== clearCallback) {
      clearRef.current = clearCallback;
    }
  }, [focusedSearch, searchBoxProps.onKeyUp]);
  
  const doSearchChange = (value: string, clearCallback?: NilFunction) => {
    disableOverlayAndClear();
    searchBoxProps.onSearchChange?.(value, clearCallback);
  };

  
  const onInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
    }
  }, []);
  const onClickInput = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    enableInputFocus();
  }

  const handleOverlayClick = () => {
    disableOverlayAndClear();
  }

  const onCloseMenu = () => {
    setMenuOpened(false);
  }
  
  const domRefs = {
    headerCenterRef,
    focusableRef,
    navMenuRef
  };
  const headerSearchHandlers = {
    handleMenuClick,
    handleOverlayClick,
    onCloseMenu,
    onClickInput,
    onInputKeyUp,
    onInputKeyDown,
    doSearchChange
  }

  return {
    domRefs,
    headerSearchHandlers,
    focusedSearch,
    setFocusedSearch,   
    menuOpened,
    setMenuOpened,
    searchValue,
    setSearchValue,
    searchBoxProps
  };
}

const useLanguageCurrencyPicker = (languageCurrencyPicker?: LanguageCurrencyPickerProps, onClick?: NilFunction) => {
  const fullScreenRef = useRef<LanguageCurrencyFullScreenHandle>(null);
  const handleFullScreenClick = () => {
    fullScreenRef.current?.openFullScreen();
    onClick?.();
  }

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>, state: { language: string, currency: string }) => {
    e.preventDefault();
    languageCurrencyPicker?.buttonOnClick?.(e, state);
    fullScreenRef.current?.closeFullScreen();
  }

  const toggleProps = {
    handleToggle: handleFullScreenClick,
    flagCountryUrl: languageCurrencyPicker?.flagCountryUrl || '',
    countryName: languageCurrencyPicker?.countryName || '',
    languageName: languageCurrencyPicker?.languageName || '',
    currencyName: languageCurrencyPicker?.currencyName || '',
    hideCurrency: languageCurrencyPicker?.hideCurrency || false,
  }
  return {fullScreenRef, handleButtonClick, handleFullScreenClick, toggleProps};
};

export {useHeaderMenuSearch, useLanguageCurrencyPicker, parseSearchBoxProps};
